/**
 * @Author: v_jppjpeng
 * @Date: 2022-10-24 15:37:35
 * @Explain: 数据判断
 */

import { ActionTypeEnum, BooleanTypeEnum } from '@/definition/common/data.d';
import { intlGet } from '@/locales';

/** 是否有效值 */
export function isEffectiveValue(value: any) {
  return value !== '' && value !== null && value !== undefined;
}

/** 是否无效值 */
export function isInvalidValue(value: any) {
  return value === '' || value === null || value === undefined;
}

/** 是否是对象 */
export function isObject(value: any) {
  return value && Object.prototype.toString.call(value) === '[object Object]';
}

/** 是否是数组 */
export function isArray(value: any) {
  return value && Object.prototype.toString.call(value) === '[object Array]';
}

/** 是否是字符串 */
export function isString(value: any) {
  return typeof value === 'string';
}

/** 是否是数字 */
export function isNumber(value: any) {
  return typeof value === 'number';
}

/** 是否是undefind */
export function isUndefind(value: any) {
  return typeof value === void 0 || typeof value === 'undefined';
}

/** 是否是函数 */
export function isFunction(value: any) {
  return typeof value === 'function';
}

/** 是否中文 */
export function isChinese(val: any) {
  const chineseRegex = /^[\u4e00-\u9fa5]+$/;
  return chineseRegex.test(val);
}

// 链接上的参数 - action
export const isAdd = (value?: string): boolean => value === ActionTypeEnum.ADD;
export const isEdit = (value?: string): boolean =>
  value === ActionTypeEnum.EDIT;
export const isDetails = (value?: string): boolean =>
  value === ActionTypeEnum.DETAILS;
export const isPreview = (value?: string): boolean =>
  value === ActionTypeEnum.PREVIEW;
export const isCopy = (value?: string): boolean =>
  value === ActionTypeEnum.COPY;

// 布尔值类型
export const isY = (value?: string): boolean => value === BooleanTypeEnum.Y;
export const isN = (value?: string): boolean => value === BooleanTypeEnum.N;

/**
 * 简易表单的区分数据格式判断方法
 */
type ProtoTypeCall = {
  otherCallBack?: (value: any) => void;
  arrayCallBack?: (value: any[]) => void;
  objectCallBack?: (value: any) => void;
};

export function prototypeCall(
  value: any,
  { otherCallBack, arrayCallBack, objectCallBack }: ProtoTypeCall,
) {
  const type = Object.prototype.toString.call(value);
  switch (type) {
    case '[object Array]':
      return arrayCallBack?.(value);
    case '[object Object]':
      return objectCallBack?.(value);
    default:
      return otherCallBack?.(value);
  }
}

/** 判断数组是否包含对象 */
export function isArrayIncludeObjects(value: any[]) {
  const data = value.filter((el) => isObject(el));
  return data?.length;
}

/** 校验数组是否为空 */
export function isArrayLen(value: any) {
  return isArray(value) && value.length;
}

/** 校验对象是否为空 */
export function isEmptyObject(obj: Record<any, any>) {
  return Object.keys(obj).length === 0;
}
/** 校验对象是否有值 */
export function isObjectLen(obj: Record<any, any>) {
  return obj && Object.keys(obj).length > 0;
}

/** 是否存在
 * key: 搜索值
 * value: 文本值
 */
export function doesItExist(key: string, text: any, compareNoCase?: boolean) {
  const newText = compareNoCase ? text.toLocaleLowerCase() : text;
  const newKey = compareNoCase ? key.toLocaleLowerCase() : key;
  return newText && newKey ? newText.indexOf(newKey) !== -1 : false;
}

/** 判断对象值是否其中一个满足条件 */
export function objectSome(
  obj: Record<any, any>,
  callback: (value: any, key: string, obj: Record<any, any>) => any,
) {
  for (let key in obj) {
    if (obj.hasOwnProperty(key)) {
      if (callback(obj[key], key, obj)) {
        return true;
      }
    }
  }
  return false;
}

/** 判断对象值是否都满足条件 */
export function objectEvery(
  obj: Record<any, any>,
  callback: (value: any, key: string, obj: Record<any, any>) => any,
) {
  for (let key in obj) {
    if (obj.hasOwnProperty(key)) {
      if (!callback(obj[key], key, obj)) {
        return false;
      }
    }
  }
  return true;
}

/** 是否正确文件类型 - 默认正确 */
export function isTheFileTypeCorrect({
  name,
  type,
}: {
  name: string;
  type: any[];
}) {
  const str = name?.split('.');
  let flag = true;
  // 因有可能出现文件名有多个点的情况，所以这样写判断
  type.forEach((el: any) => {
    flag = str.includes(el);
  });
  return flag;
}

/**
 * 数组对象, 判断传过来的Key是否等于 judgeValue
 * @param arr 循环的数组
 * @param Key 需要判断的key
 * @param judgeValue 需要判断的值
 * @returns boolean
 */

export const arrayObjectEqualKey = (
  arr: any[],
  key: string,
  judgeValue: string,
) => {
  if (key) {
    // eslint-disable-next-line no-restricted-syntax
    for (const item of arr) {
      if (item[key] === judgeValue) {
        return true;
      }
    }
  }
  return false;
};

// 级联模糊搜索 - 按照原结构输出
export const filterTreeData = ({
  tree,
  searchValue,
  onText,
}: {
  tree: any[];
  searchValue: string;
  onText: (val: any) => string;
}) => {
  let result: any[] = [];
  tree.forEach((node) => {
    // 深拷贝当前节点，避免直接修改原数据
    let newNode = { ...node };
    // 如果当前节点的标题包含搜索值，则保留该节点
    if (
      onText(newNode)?.toLowerCase().indexOf(searchValue?.toLowerCase()) > -1
    ) {
      result.push(newNode);
    } else if (
      newNode.children &&
      filterTreeData({ tree: newNode.children, searchValue, onText }).length > 0
    ) {
      // 如果当前节点的子节点包含搜索值，则保留该节点

      // 递归查询处理子节点
      if (newNode.children && newNode.children.length > 0) {
        newNode.children = filterTreeData({
          tree: newNode.children,
          searchValue,
          onText,
        });
      }
      result.push(newNode);
    }
  });
  return result;
};

/** 级联是否存在
 * key: 搜索值
 * value: 文本值
 */
export function cascaderDoesItExist({
  list,
  searchValue,
}: {
  list: any[];
  searchValue: string;
}) {
  const onText = (item: any) =>
    intlGet(item.tipsKey) ||
    item.tips ||
    intlGet(item?.labelKey) ||
    item?.label ||
    '';
  let optionsRes = filterTreeData({ tree: list, searchValue, onText });
  return optionsRes;
}

/** 比较两个数组内容是否一致 */
export const areArraysContentEqual = ({
  arr1 = [],
  arr2 = [],
}: {
  arr1: any[];
  arr2: any[];
}) => {
  arr2 = [...arr2];
  if (arr1.length !== arr2.length) return false;
  arr1.some((item) => {
    const index = arr2.findIndex((item1) => {
      if (Number.isNaN(item) && Number.isNaN(item1)) return true;
      return item === item1;
    });
    if (index !== -1) {
      arr2.splice(index, 1);
    }
  });
  return !arr2.length;
};

/** 将树形结构数据处理为从根到分支顶端链路处理为一组数据 */
export const getAllPathsFromRoot = ({
  tree,
  accumulator = [],
  paths = [],
}: {
  tree: any[];
  accumulator?: any[];
  paths?: any[];
}) => {
  for (let node of tree) {
    const newPath: any = [...accumulator, node];
    if (node.children && node.children.length > 0) {
      getAllPathsFromRoot({ tree: node.children, accumulator: newPath, paths });
    } else {
      paths.push(newPath);
    }
  }
  return paths;
};

// 将级联二维数字或字符串数组 转为树形结构
function arr2List(arr: (number | string)[]) {
  const list = [];
  const head = {
    option: arr[0],
    optionValue: [],
  };
  let prev: any = head;
  list.push(prev);
  for (let i = 1; i < arr.length; i++) {
    const current: any = {
      option: arr[i],
      optionValue: [],
    };
    prev.optionValue.push(current);
    prev = current;
  }
  return list;
}

// 将级联二维数字或字符串数组 转为树形结构
function mergeList(tree: any[], list: any) {
  if (!tree) {
    return;
  }
  const hasCommonParent = tree.some((treeItem) => {
    // 有共同的父节点
    if (treeItem.name === list[0].name) {
      mergeList(treeItem.optionValue, list[0].optionValue);
      return true;
    }
    return false;
  });

  if (!tree.length || !hasCommonParent) {
    tree.push(list[0]);
  }
}

// 将级联二维数字或字符串数组 转为树形结构
export function arr2Tree(arr: (number | string)[][]) {
  const tree: any[] = [];
  arr.forEach((item) => {
    const list = arr2List(item);
    mergeList(tree, list);
  });
  return tree;
}
